// The پارسه price list, resolved — ONE server-side list instead of the
// hardcoded constants (docs/AI_COST_AND_PARSEH_PRICING_MANAGEMENT_DESIGN.md
// §9.2, phase 3).
//
// Every price this app prints goes through `costOf(actionId)` below. The
// action ids are the ones the app already used (`COIN_COSTS.TOOTH_FINDINGS`,
// …); `SERVICE_KEY_BY_ACTION` is the ONE table that maps them onto the AI RPC
// names the service prices — change a mapping here, nowhere else.
//
// ── The constants are the fallback and the first paint ──────────────────────
// `constants/creditPricing.js` is what renders before the list has arrived and
// what renders when it never does (offline, BFF down, the AI service down and
// the BFF serving its own frozen copy). A price chip therefore NEVER spins and
// never goes blank: it shows the shipped number, then the policy's number the
// moment it is known. `source` says which one is on screen.
//
// ── Fetched once per page load, remembered for the session ──────────────────
// The list is read through the app's own GraphQL client the first time any
// surface asks for it and shared by every component after that (a module-level
// store read through `useSyncExternalStore`). The last answer is also kept in
// sessionStorage so a reload paints the policy price first, not the constant.
// A FAILED read is not remembered: the next surface to mount tries again.

import { useEffect, useSyncExternalStore } from 'react';
import { bffQuery } from './bffClient.js';
import { COIN_COSTS, COIN_PRICE_TOMAN, PRICE_LIST } from '../constants/creditPricing.js';

/** Client action id → the AI RPC the price list prices it under. */
export const SERVICE_KEY_BY_ACTION = Object.freeze({
  TRANSCRIPTION_PER_MIN: 'TranscribeAudio',
  DIFFERENTIAL: 'GetDifferentialDiagnosis',
  CONFIRM_QUESTIONS: 'GenerateConfirmationQuestions',
  ASK_QUESTION: 'AskDoctorQuestion',
  SOAP_REFINE: 'RefineSoapNote',
  TOOTH_FINDINGS: 'ExtractToothFindings',
  // `generateVisitAdvice` in parsina.bff.dentist is served by GenerateVisitSummary.
  VISIT_ADVICE: 'GenerateVisitSummary',
  OCR: 'ProcessDocumentOCR',
  INTERACTION_CHECK: 'CheckDrugInteraction',
});

export const CREDIT_PRICES_QUERY = `
  query CreditPrices {
    creditPrices {
      source
      policyId
      parsehToman
      generatedAt
      items { serviceKey unit parseh priceSource }
    }
  }
`;

const STORAGE_KEY = 'parsina:creditPrices:v1';

const FALLBACK_STATE = Object.freeze({
  byService: null,          // Map<serviceKey, parseh> once a service list is held
  parsehToman: null,        // the policy's Toman value of one پارسه, when known
  policyId: null,
  source: 'fallback',       // 'service' | 'fallback' — what is on screen
  loaded: false,            // has any read finished (either way) this page load
});

let state = FALLBACK_STATE;
const listeners = new Set();
let inflight = null;
let answered = false;       // a SERVICE answer landed this page load — read once

function emit(next) {
  state = next;
  for (const fn of listeners) fn();
}

/** A GraphQL `creditPrices` answer → store state. Exported for tests. */
export function stateFromAnswer(list) {
  if (!list || list.source !== 'service' || !Array.isArray(list.items)) {
    // The BFF's own fallback is still the constants — say so, and keep them.
    return { ...FALLBACK_STATE, loaded: true };
  }
  const byService = new Map();
  for (const item of list.items) {
    // An unpriced row (null) leaves the constant in place: a chip must never
    // go blank for a service the AI still charges under its code default.
    // Checked for null BEFORE Number(): Number(null) is 0, and 0 is a price.
    if (!item?.serviceKey || item.parseh === null || item.parseh === undefined) continue;
    const n = Number(item.parseh);
    if (Number.isFinite(n) && n >= 0) byService.set(item.serviceKey, n);
  }
  const toman = Number(list.parsehToman);
  return {
    byService,
    parsehToman: Number.isFinite(toman) && toman > 0 ? Math.round(toman) : null,
    policyId: list.policyId || null,
    source: 'service',
    loaded: true,
  };
}

/** Feed an answer straight into the store (tests, or a surface that already fetched it). */
export function applyCreditPrices(list) {
  const next = stateFromAnswer(list);
  if (next.source === 'service') {
    answered = true;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* private mode, quota — the constants apply */ }
  }
  emit(next);
}

/** Back to the constants. Tests only. */
export function resetCreditPrices() {
  state = FALLBACK_STATE;
  inflight = null;
  answered = false;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* no storage */ }
  for (const fn of listeners) fn();
}

// A reload paints the last policy price first, not the constant.
try {
  const cached = sessionStorage.getItem(STORAGE_KEY);
  if (cached) {
    const restored = stateFromAnswer(JSON.parse(cached));
    if (restored.source === 'service') state = { ...restored, loaded: false };
  }
} catch { /* no storage, or a stale shape — the constants apply */ }

/**
 * Read the list once. Concurrent callers share one request; a finished SERVICE
 * answer is never re-read this page load; a failure is forgotten so the next
 * mount retries. Never rejects — a price chip has a fallback for every outcome.
 */
export function loadCreditPrices() {
  if (answered) return Promise.resolve(state);
  if (inflight) return inflight;
  inflight = Promise.resolve()
    .then(() => bffQuery(CREDIT_PRICES_QUERY))
    .then((data) => {
      const list = data?.creditPrices;
      if (list && list.source === 'service') applyCreditPrices(list);
      else emit({ ...state, loaded: true });
    })
    .catch(() => {
      // Offline, signed out, the BFF down: the constants stay on screen.
      emit({ ...state, loaded: true });
    })
    .finally(() => { inflight = null; })
    .then(() => state);
  return inflight;
}

/**
 * The پارسه price of one client action, RIGHT NOW: the policy's figure once
 * the list has arrived, the shipped constant until then. Synchronous. `null`
 * for an action the app does not price at all.
 */
export function costOf(actionId) {
  const key = SERVICE_KEY_BY_ACTION[actionId];
  if (key && state.byService && state.byService.has(key)) return state.byService.get(key);
  return COIN_COSTS[actionId] ?? null;
}

/** Retail Toman value of one پارسه: the policy's, else the shipped constant. */
export function coinPriceToman() {
  return state.parsehToman ?? COIN_PRICE_TOMAN;
}

/** The Credits-page rows with their costs resolved the same way. */
export function resolvedPriceList() {
  return PRICE_LIST.map((row) => ({ ...row, cost: costOf(row.action) }));
}

const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const getSnapshot = () => state;

/**
 * The hook every price surface reads through. Triggers the one read on first
 * use and re-renders the caller when the answer lands.
 */
export function useCreditPrices() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { loadCreditPrices(); }, []);
  return {
    costOf,
    coinPriceToman: snapshot.parsehToman ?? COIN_PRICE_TOMAN,
    priceList: resolvedPriceList(),
    source: snapshot.source,
    policyId: snapshot.policyId,
    loaded: snapshot.loaded,
  };
}
